package fr.frux.core.ads

import kotlinx.coroutines.flow.StateFlow

/**
 * Politique publicitaire commune (ADR 0030, #527) : session, droits Frux+, consentement et
 * arbitrage de l'emplacement unique. Les écrans lisent [state] et ne parlent jamais au SDK.
 *
 * Surface limitée à l'usage réel (#108) : la liste native (#530) et la vidéo récompensée (#531)
 * ajouteront leurs membres.
 */
interface AdsController {
    val state: StateFlow<AdsState>

    /**
     * Réclame [placement] tant qu'il est à l'écran (bannière de carte #529, plus tard la liste native).
     * L'annonce ne s'affiche que si [AdsState.visibleClaimId] vaut [AdClaim.id] : la priorité la plus
     * haute l'emporte, à égalité la claim la plus ancienne. [AdClaim.release] est idempotent.
     */
    fun claim(placement: AdPlacement): AdClaim

    /** Suspend toute publicité jusqu'à [AdHandle.release]. */
    fun hold(reason: AdHold): AdHandle

    /** « Continuer » sur l'écran d'information : ouvre le formulaire UMP. */
    fun onConsentIntroContinue()

    /** Écran d'information écarté : rien jusqu'à la session suivante. */
    fun onConsentIntroDismissed()

    /** Entrée Profil « Gérer mes choix publicitaires ». `false` si le formulaire n'a pas pu s'ouvrir. */
    suspend fun openPrivacyOptions(): Boolean

    /**
     * Tente d'afficher l'interstitiel à une pause naturelle (ADR 0030 point 2, #528). Jamais suspend
     * et n'attend rien : `false` si l'annonce n'est pas prête à cet instant, elle n'est pas rattrapée.
     *
     * Montre l'annonce seulement si toutes ces conditions tiennent : publicité permise
     * ([AdsState.eligible], donc ni première session, ni compte sans pub, ni hold), aucun interstitiel
     * déjà vu dans cette session (persisté, une mort du process ne le réarme pas), annonce préchargée
     * et pas de flux sensible « juste avant ».
     *
     * « Juste après » un flux sensible : tant qu'un [AdHold.SENSITIVE_FLOW] est posé, rien. Au premier
     * appel qui suit la libération du dernier de ces holds, rien non plus : cet appel consomme la
     * garde, quel que soit son déclencheur (le retour à la carte qui suit un ajout d'arbre est ce
     * premier appel). Les appels suivants sont jugés normalement.
     */
    fun maybeShowInterstitial(trigger: InterstitialTrigger): Boolean

    /**
     * Vidéo récompensée proposable (#531) : publicité éligible pour ce compte (même calcul que les
     * autres emplacements, donc ni Frux+, ni `AD_FREE`, ni première session, ni hold), compte connecté,
     * bloc configuré, aucune vidéo en cours. Anonyme : toujours faux.
     */
    val canOfferRewarded: StateFlow<Boolean>

    /**
     * Fenêtre d'attente de 5 minutes ouverte par un [RewardedResult.PENDING] : la vidéo est vue mais le
     * droit `AD_FREE` n'est pas encore visible. [canOfferRewarded] est faux pendant ce temps. Se ferme
     * dès que le droit apparaît, à l'échéance, ou à la déconnexion. Vit avec le contrôleur, donc
     * survit à la navigation.
     */
    val rewardedPending: StateFlow<Boolean>

    /**
     * « Regarder une vidéo pour 24 h sans pub » (ADR 0030 point 8A, #531). Réclame l'emplacement
     * [AdPlacement.REWARDED], charge puis montre la vidéo. Vue jusqu'au bout, relit les droits à 1,
     * 2, 3 puis 4 secondes : [RewardedResult.GRANTED] si le droit du serveur est visible, sinon
     * [RewardedResult.PENDING] et une dernière relecture en arrière-plan à 30 secondes. La claim est
     * libérée dans tous les cas. Un second appel pendant une vidéo rend [RewardedResult.DISMISSED]
     * sans rien charger.
     */
    suspend fun watchRewardedForAdFree(): RewardedResult
}

/** Ce que l'écran dit après une vidéo récompensée (#531). [DISMISSED] : rien. */
enum class RewardedResult { GRANTED, PENDING, DISMISSED, UNAVAILABLE }
